import { ThisConnector } from '../../../../api/connector'
import { AggregationConfigs, Aggregations } from '../../../aggregations'
import { kebabCaseToCamelCase } from '../../../text-transformation'
import {
  getNormalizedFacetName,
  isApplicationMetadataFacet
} from '../../header/filters/vr-facet-name-helper'

interface SelectedValueReportingFacet {
  facetName: string
  facetValue: string
}

export interface ApplicationSecuritySummaryFacetsRequestParams {
  organizationUuid: string
  selectedFacets?: SelectedValueReportingFacet[]
}

export async function applicationSecuritySummaryFacetsRequest(
  this: ThisConnector,
  {
    organizationUuid,
    selectedFacets
  }: ApplicationSecuritySummaryFacetsRequestParams
) {
  const apiResponse = await this.connector.post<{
    facets: ApiApplicationFacet[]
  }>(`/organizations/${organizationUuid}/application-security-summary/facets`, {
    facets: selectedFacets
  })

  const apiFacets = apiResponse.facets ? apiResponse.facets : []

  return {
    totalCount: getTotalCount(apiFacets),
    aggregations: normalizeFacets(apiFacets),
    applicationMetadataConfigurations:
      createApplicationMetadataConfigurations(apiFacets)
  }
}

export function normalizeFacets(
  facets: ApiApplicationFacet[]
): Aggregations<string> {
  return facets.reduce<Aggregations<string>>((prev, facet) => {
    const name = getNormalizedFacetName(facet.name, kebabCaseToCamelCase)
    return {
      ...prev,
      [name]: facet.values.map(({ count, value, displayName }) => {
        return {
          key: {
            label: displayName,
            id: value
          },
          count
        }
      })
    }
  }, {})
}

function createApplicationMetadataConfigurations(
  facets: ApiApplicationFacet[]
) {
  return facets.reduce<AggregationConfigs<string>>((prev, facet) => {
    const name = getNormalizedFacetName(facet.name, kebabCaseToCamelCase)
    return isApplicationMetadataFacet(name)
      ? {
          ...prev,
          [name]: {
            groupDisplayName: facet.displayName,
            bucketToID: ({ key: { id } }) => id,
            bucketToString: ({ key: { label } }) => label
          }
        }
      : prev
  }, {})
}

interface ApiApplicationFacetValueType {
  value: string
  displayName: string
  count?: number
}

export interface ApiApplicationFacet {
  name: string
  displayName: string
  totalCount: number
  values: ApiApplicationFacetValueType[]
}

function getTotalCount(facets: ApiApplicationFacet[]) {
  return facets.length > 0 ? facets[0].totalCount : undefined
}
